import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { calcDoc, fmt, genNum, today } from '../../utils/calc';
import { buildAbonnementFields } from '../../utils/subscription';
import '../modals/Modal.css';

// ── Chargement SDK FedaPay (même schéma que FactureModal) ──────
function useFedaPay() {
  const [ready, setReady] = useState(!!window.FedaPay);
  useEffect(() => {
    if (window.FedaPay) { setReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.fedapay.com/checkout.js?v=1.1.7';
    s.async = true;
    s.onload  = () => setReady(true);
    s.onerror = () => console.error('FedaPay SDK non chargé');
    document.body.appendChild(s);
  }, []);
  return ready;
}

// ── Appel Cloud Function : création du compte client après paiement ──
const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_BASE_URL ||
                        'https://europe-west1-VOTRE_PROJET.cloudfunctions.net';
const KOTOS_API_KEY = process.env.REACT_APP_KOTOS_API_KEY;

async function createClientAccount({ commandeId, email, phone, nom }) {
  try {
    const resp = await fetch(`${FUNCTIONS_BASE}/createClientAccount`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiKey: KOTOS_API_KEY, commandeId, email, phone, nom }),
    });
    return await resp.json();
  } catch (e) {
    console.error('createClientAccount fetch error:', e);
    return { ok: false, error: 'Service de compte client indisponible.' };
  }
}

export default function CheckoutModal({ onClose }) {
  const {
    data, cart, cartTotal, clearCart, setShowCart,
    saveTransaction, updateTransaction, showToast,
  } = useApp();
  const fedaReady = useFedaPay();

  const [cnom,   setCnom]   = useState('');
  const [ctel,   setCtel]   = useState('');
  const [cemail, setCemail] = useState('');
  const [ctelPay, setCtelPay] = useState(''); // numéro Mobile Money de paiement, si différent du contact
  const [payMethod, setPayMethod] = useState('mobile_money'); // 'mobile_money' | 'card'
  const [savedId,   setSavedId]   = useState(null);
  const [payStatus, setPayStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [accountInfo, setAccountInfo] = useState(null); // { loginEmail, tempPassword, isNewAccount }

  const lines = cart.map(it => ({
    desc: `${it.icon} ${it.name}${it.unit === 'mois' ? ' (abonnement mensuel)' : it.unit === 'analyse' ? ' (à l\'analyse)' : ''}`,
    qty: it.qty, unit: it.unit === 'mois' ? 'mois' : 'unité', prix: it.unitPrice,
  }));
  const totals = calcDoc(lines, 0, 0, 0);
  const aboFields = buildAbonnementFields(cart, data.produits || [], today());

  async function ensureSaved() {
    if (savedId) return savedId;
    const num = genNum('CMD');
    const id = await saveTransaction(
      'commande', lines,
      {
        num, date: today(), valid: 0, cnom, ctel, cemail, cadr: '',
        produitIds: cart.map(it => it.id),
        payMethodChoisi: payMethod === 'card' ? 'Carte bancaire' : 'Mobile Money',
        ctelPay: ctelPay || ctel,
        notes: 'Commande KotosShop — paiement en ligne FedaPay.',
        ...aboFields,
      },
      totals
    );
    setSavedId(id);
    return id;
  }

  async function handlePay() {
    if (!cnom || !ctel) {
      showToast('Renseignez votre nom et téléphone', 'warn');
      return;
    }
    if (!fedaReady || !window.FedaPay) {
      showToast('SDK FedaPay non chargé', 'error');
      return;
    }

    const txId = await ensureSaved();
    setPayStatus('loading');
    const numeroPaiement = (ctelPay || ctel)?.replace(/\s/g, '') || '';

    window.FedaPay.init({
      public_key: process.env.REACT_APP_FEDAPAY_PUBLIC_KEY ||
                  'pk_live_5ZFG7_VsT85YMgYhpxSx4W04',
      transaction: {
        amount:      Math.round(cartTotal),
        description: `Commande KotosShop – ${cnom} – ${cart.length} application(s)`,
      },
      customer: {
        firstname: cnom || 'Client',
        lastname:  '',
        email:     cemail || '',
        phone_number: {
          number:  numeroPaiement,
          country: 'BJ',
        },
      },
      currency: { iso: 'XOF' },
      onComplete: async function(resp) {
        if (resp.reason === window.FedaPay.DIALOG_DISMISSED) {
          setPayStatus(null);
          showToast('Paiement annulé', 'warn');
          return;
        }
        const tx = resp.transaction;
        if (tx?.status === 'approved') {
          setPayStatus('success');
          await updateTransaction(txId, {
            fedapay_status: 'approved',
            fedapay_tx_id:  String(tx.id),
            fedapay_date:   new Date().toLocaleDateString('fr-FR'),
            fedapay_mode:   tx.payment_method || payMethod,
            statut:         'payé',
          });
          showToast('✅ Paiement confirmé ! Commande enregistrée.', 'ok');

          // Création automatique du compte client (espace client dans l'app achetée)
          const acc = await createClientAccount({ commandeId: txId, email: cemail, phone: ctel, nom: cnom });
          if (acc.ok) setAccountInfo(acc);

          clearCart();
        } else {
          setPayStatus('error');
          showToast('❌ Paiement échoué ou refusé', 'error');
        }
      },
    }).open();
  }

  function handleCloseAll() {
    onClose();
    if (payStatus === 'success') setShowCart(false);
  }

  return (
    <div className="overlay open" style={{ zIndex: 9200 }}>
      <div className="modal modal-md">
        <div className="mhead">
          <h2>🛒 Finaliser ma commande</h2>
          <button className="mclose" onClick={handleCloseAll}>✕</button>
        </div>
        <div className="mbody">

          {payStatus === 'success' ? (
            <div style={{ textAlign: 'center', padding: '20px 10px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h3 style={{ color: 'var(--green)', marginBottom: 8 }}>Paiement confirmé !</h3>
              <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>
                Merci {cnom} ! Votre commande a bien été enregistrée et payée.
                Notre équipe vous contacte sous peu au {ctel} pour la mise en service.
                {aboFields.abonnement && aboFields.essaiGratuit && (
                  <> Votre mois d'essai gratuit court jusqu'au <strong>{aboFields.dateProchainPaiement}</strong>.</>
                )}
              </p>

              {accountInfo && (
                <div style={{ marginTop: 16, textAlign: 'left', background: 'rgba(0,191,255,0.06)', border: '1px solid rgba(0,191,255,0.25)', borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ fontWeight: 700, color: '#00BFFF', fontSize: 13, marginBottom: 8 }}>🔑 Votre espace client</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.7 }}>
                    Identifiant : <strong>{accountInfo.loginEmail}</strong><br />
                    {accountInfo.isNewAccount && accountInfo.tempPassword ? (
                      <>Mot de passe provisoire : <strong>{accountInfo.tempPassword}</strong><br />
                      <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>Connectez-vous dans l'application achetée et changez ce mot de passe dès que possible.</span></>
                    ) : (
                      <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>Compte déjà existant — utilisez votre mot de passe habituel.</span>
                    )}
                  </div>
                </div>
              )}

              <button className="abtn abtn-blue" style={{ marginTop: 18 }} onClick={handleCloseAll}>
                Fermer
              </button>
            </div>
          ) : (
            <>
              {/* Récapitulatif */}
              <h3 className="section-sep" style={{ marginTop: 0 }}>Récapitulatif</h3>
              {cart.map(it => (
                <div key={it.id} style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 13, padding: '6px 0', color: 'var(--muted)',
                }}>
                  <span>{it.icon} {it.name} × {it.qty}</span>
                  <span style={{ color: 'var(--text)' }}>{fmt(it.unitPrice * it.qty)}</span>
                </div>
              ))}
              <div className="totals-box">
                <div className="tot-row big"><span>TOTAL :</span><span>{fmt(cartTotal)}</span></div>
              </div>
              {aboFields.abonnement && (
                <div style={{ fontSize: 12, color: '#00BFFF', background: 'rgba(0,191,255,0.08)', border: '1px solid rgba(0,191,255,0.25)', borderRadius: 8, padding: '8px 12px', marginTop: 8 }}>
                  {aboFields.essaiGratuit
                    ? <>🎁 Cette commande inclut un abonnement avec <strong>1 mois d'essai gratuit</strong>. Premier prélèvement prévu le <strong>{aboFields.dateProchainPaiement}</strong>.</>
                    : <>🔁 Cette commande inclut un abonnement mensuel. Prochain paiement prévu le <strong>{aboFields.dateProchainPaiement}</strong>.</>}
                </div>
              )}

              {/* Infos client */}
              <h3 className="section-sep">Vos coordonnées</h3>
              <div className="grid2">
                <div className="fg2"><label>Nom complet *</label>
                  <input value={cnom} onChange={e => setCnom(e.target.value)} placeholder="Votre nom" />
                </div>
                <div className="fg2"><label>Téléphone de contact *</label>
                  <input value={ctel} onChange={e => setCtel(e.target.value)} placeholder="+229 XX XX XX XX" />
                </div>
              </div>
              <div className="fg2"><label>Email (reçu de paiement)</label>
                <input type="email" value={cemail} onChange={e => setCemail(e.target.value)} placeholder="vous@email.com" />
              </div>
              <div className="fg2">
                <label>Numéro Mobile Money pour le paiement (si différent du contact)</label>
                <input value={ctelPay} onChange={e => setCtelPay(e.target.value)} placeholder={ctel || '+229 XX XX XX XX'} />
              </div>

              {/* Paiement */}
              <div style={{
                margin: '16px 0 4px', padding: '16px',
                background: 'rgba(0,204,136,0.05)', border: '1px solid rgba(0,204,136,0.25)',
                borderRadius: '10px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>💳</span>
                  <span style={{ fontWeight: 700, color: '#00CC88', fontSize: 14 }}>Paiement sécurisé</span>
                </div>

                <div className="tab-desc" style={{ marginBottom: 10 }}>
                  Choisissez votre mode préféré ci-dessous. La fenêtre de paiement FedaPay qui s'ouvre
                  affichera ensuite les canaux réellement activés sur notre compte marchand
                  (Mobile Money et/ou Carte bancaire) — cette liste dépend de l'activation en cours de notre compte FedaPay.
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <button
                    type="button"
                    onClick={() => setPayMethod('mobile_money')}
                    style={{
                      flex: 1, padding: '10px', borderRadius: 8, cursor: 'pointer',
                      border: payMethod === 'mobile_money' ? '2px solid #00CC88' : '1px solid rgba(255,255,255,0.15)',
                      background: payMethod === 'mobile_money' ? 'rgba(0,204,136,0.12)' : 'rgba(255,255,255,0.04)',
                      color: payMethod === 'mobile_money' ? '#00CC88' : 'var(--muted)', fontWeight: 700, fontSize: 12,
                    }}
                  >
                    📱 Mobile Money<br /><span style={{ fontSize: 10, fontWeight: 400 }}>MTN · Moov · Celtis</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPayMethod('card')}
                    style={{
                      flex: 1, padding: '10px', borderRadius: 8, cursor: 'pointer',
                      border: payMethod === 'card' ? '2px solid #00BFFF' : '1px solid rgba(255,255,255,0.15)',
                      background: payMethod === 'card' ? 'rgba(0,191,255,0.12)' : 'rgba(255,255,255,0.04)',
                      color: payMethod === 'card' ? '#00BFFF' : 'var(--muted)', fontWeight: 700, fontSize: 12,
                    }}
                  >
                    💳 Carte bancaire<br /><span style={{ fontSize: 10, fontWeight: 400 }}>Visa · Mastercard</span>
                  </button>
                </div>

                {payStatus === 'error' && (
                  <div style={{ background: 'rgba(255,64,96,0.15)', border: '1px solid #FF4060', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#FF4060', fontWeight: 700, fontSize: 13 }}>
                    ❌ Paiement échoué. Veuillez réessayer.
                  </div>
                )}

                <button
                  className="abtn abtn-green"
                  style={{ width: '100%', justifyContent: 'center', fontSize: 14, padding: '12px' }}
                  onClick={handlePay}
                  disabled={payStatus === 'loading'}
                >
                  {payStatus === 'loading' ? '⏳ Traitement en cours...' : `💳 Payer ${fmt(cartTotal)}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
